import * as React from 'react'
import { Sun, Moon, Globe, Download } from 'lucide-react'
import { Tooltip } from '@mui/material'

import usePageInfo from '../../hooks/usePageInfo'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
    isGraphOnlyMode,
    getCurrentContext,
} from '../LeftMenuMigration/legacyBridge'
import LanguageSettingsDialog from '../LanguageSettingsDialog'
import DownloadDialog from '../DownloadDialog'
import { usePanelStore } from '../../stores/usePanelStore'

const GRAPH_PAGES = ['context', 'conceptsSynthesis', 'synthesis', 'search']

/**
 * Floating vertical tools bar anchored to the top-left corner, sitting
 * just below the AppBar. Z-index is below the header so the header
 * always paints over it if anything overlaps.
 *
 * Hosts (top → bottom): Language Settings, Download, divider, Theme.
 *
 * Render gates:
 *   - graph pages only (context / synthesis views)
 *   - never in graph-only mode (`?hide_max=1` URL param or
 *     `window.top !== window.self`)
 *
 * Mobile (≤600px) gets a more compact variant with smaller padding.
 *
 * Tailwind: classes use the `tw-` prefix per project tailwind.config.js.
 * Wrapped in `.project-notes-scope` so utilities pick up the project's
 * `!important` scope (a shared trick to override legacy CSS).
 */
export default function GraphToolsBar() {
    const { location } = usePageInfo()
    const isMobile = useMediaQuery('(max-width:600px)')
    const [isDark, setIsDark] = React.useState(() =>
        document.documentElement.hasAttribute('theme')
    )
    const [graphOnly] = React.useState(() => isGraphOnlyMode())
    const [languageOpen, setLanguageOpen] = React.useState(false)
    const [downloadOpen, setDownloadOpen] = React.useState(false)
    const hasContext = !!getCurrentContext()
    const isMobileFilterOptionsOpen = usePanelStore(
        state => state.isMobileFilterOptionsOpen
    )

    // Subscribe to theme changes. The legacy `switchThemeColor` in
    // `entries/eventEmitter.ejs` was reordered to update the DOM attribute
    // *before* emitting `themeChange`, so reading the attribute
    // synchronously inside the listener now catches the new value.
    React.useEffect(() => {
        const readDom = () =>
            setIsDark(document.documentElement.hasAttribute('theme'))
        readDom()
        const emitter = window.myEventEmitter
        if (!emitter) return
        emitter.on('themeChange', readDom)
        return () => emitter.removeListener?.('themeChange', readDom)
    }, [])

    // Let legacy EJS (e.g. the Binders.ejs workflow stepper) open/close the
    // Export Data dialog, which is otherwise only reachable via the download
    // button's local state. Mirrors the themeChange bridge above.
    React.useEffect(() => {
        const emitter = window.myEventEmitter
        if (!emitter) return
        const open = () => setDownloadOpen(true)
        const close = () => setDownloadOpen(false)
        emitter.on('showExportData', open)
        emitter.on('hideExportData', close)
        return () => {
            emitter.removeListener?.('showExportData', open)
            emitter.removeListener?.('hideExportData', close)
        }
    }, [])

    // Let other components (e.g. GraphEmptyState's "change processing
    // settings" link) open the Graph Language & Processing dialog.
    React.useEffect(() => {
        const emitter = window.myEventEmitter
        if (!emitter) return
        const open = () => setLanguageOpen(true)
        emitter.on('showLanguageSettings', open)
        return () => emitter.removeListener?.('showLanguageSettings', open)
    }, [])

    const handleThemeToggle = () => {
        if (typeof toggleTheme === 'function') {
            toggleTheme(true)
        }
    }

    const isGraphPage = GRAPH_PAGES.includes(location)
    if (!isGraphPage || graphOnly) return null

    // Vertical orientation, top-left corner, just below the 48px AppBar.
    // z-30 sits below the AppBar's z-index so the header paints over the
    // pill if anything ever overlaps.
    // `tw-border-solid` is required: Tailwind preflight is disabled in this
    // repo, so `tw-border` only sets width and the border is invisible
    // without explicit border-style. (Tracked: scoped reset deferred.)
    const containerBase =
        'tw-fixed tw-z-30 tw-flex tw-flex-col tw-items-center tw-rounded-full tw-bg-white/90 dark:tw-bg-[#1e1e2e]/85 tw-backdrop-blur-md tw-border tw-border-solid tw-border-gray-200/80 dark:tw-border-gray-700/60 tw-shadow-md tw-pointer-events-auto'
    const containerClass = isMobile
        ? `${containerBase} tw-gap-1.5 tw-py-1.5 tw-px-0.5`
        : `${containerBase} tw-gap-2 tw-py-2 tw-px-1`
    const buttonClass = isMobile
        ? 'tw-p-1 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100/80 dark:hover:tw-bg-gray-700/60 tw-text-gray-700 dark:tw-text-gray-200 tw-transition-colors tw-cursor-pointer tw-border-0 tw-bg-transparent'
        : 'tw-p-1 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100/80 dark:hover:tw-bg-gray-700/60 tw-text-gray-700 dark:tw-text-gray-200 tw-transition-colors tw-cursor-pointer tw-border-0 tw-bg-transparent'

    // On mobile, when the StatementFilterPanel's "more options" dropdown
    // is open, it stacks 3 captioned buttons in the same left column where
    // this tools bar sits — push the bar down so they don't overlap.
    const containerStyle = {
        top:
            isMobile && isMobileFilterOptionsOpen
                ? '320px'
                : '174px',
        left: isMobile ? '0.5rem' : '0.5rem',
        transition: 'top 0.2s ease-in-out',
    }

    return (
        <div className="project-notes-scope">
            <div
                data-testid="graph-tools-bar"
                style={containerStyle}
                className={containerClass}
            >
                {/* Language Settings — context-only. Synthesis views have no
                    single context to apply lemmatizer / stopwords to. */}
                {hasContext && (
                    <Tooltip
                        title="Graph language"
                        arrow={false}
                        placement="right"
                    >
                        <button
                            type="button"
                            onClick={() => setLanguageOpen(true)}
                            aria-label="language-settings"
                            className={buttonClass}
                        >
                            <Globe size={16} />
                        </button>
                    </Tooltip>
                )}
                {/* Download / Export */}
                <Tooltip
                    title="Download / export"
                    arrow={false}
                    placement="right"
                >
                    <button
                        type="button"
                        onClick={() => setDownloadOpen(true)}
                        aria-label="download"
                        className={buttonClass}
                    >
                        <Download size={16} />
                    </button>
                </Tooltip>
                {/* Divider — horizontal line between graph tools and the
                    theme switch (the vertical equivalent of the previous
                    horizontal-bar's vertical divider). */}
                <span
                    aria-hidden="true"
                    className="tw-block tw-h-px tw-w-4 tw-bg-gray-300/80 dark:tw-bg-gray-600/60 tw-my-1"
                />
                <Tooltip
                    title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
                    arrow={false}
                    placement="right"
                >
                    <button
                        type="button"
                        onClick={handleThemeToggle}
                        aria-label={`Switch to ${
                            isDark ? 'light' : 'dark'
                        } theme`}
                        className={buttonClass}
                    >
                        {isDark ? <Sun size={16} /> : <Moon size={16} />}
                    </button>
                </Tooltip>
            </div>
            <LanguageSettingsDialog
                open={languageOpen}
                onClose={() => setLanguageOpen(false)}
            />
            <DownloadDialog
                open={downloadOpen}
                onClose={() => setDownloadOpen(false)}
            />
        </div>
    )
}
